import React, { useContext } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { FormikProps } from 'formik';
import { useRouter } from 'next/router';
import * as Yup from 'yup';
import { forgotPassword } from '../../../interfaces/password';
import { useLogin } from '../../../lib/useLogin';
import { Dialog, DialogContent, DialogHeader } from '../../dialog';
import { MessageResetPasswordContent, ResetPasswordForm } from '../Passwords';
import { Button, ButtonProps, Form, FormikValues, FormWrap } from '../../formikFormInputs';
import LoginText from './LoginText';
import { Agreement, LoginFields } from './index';
import { save } from '../../../utils/storage';
import { AGENT_SIGNING_KEY, AUTH_SESSION_KEY, initFalse } from '../../../utils/constants';
import {
  checkSellerAgreementSignValid,
  handleFormError,
  redirectIfPreferred,
  stringify,
  verifyLastSession
} from '../../../utils/commonUtils';
import { refreshAccessToken } from '../../../lib/privateApi';
import { SnackbarContext } from '../../Snackbar/Context';
import { LoginType, Paths, ROLES, SnackBarSeverity } from '../../../utils/enums';
import { useAuth } from '../../../lib/useAuth';
import { REQUIRED } from '../../../utils/validation';
import { fetcher } from '../../Auth/AuthProvider';


enum Account {
  Login,
  ResetPassword,
  MessageResetPassword
}

type LoginDialogProps = {
  close: () => void;
  isOpen: boolean;
  userType: string;
}

type LoginContentProps = {
  close: Function;
  onForgotPassword: Function;
  userType: string;
}

interface LoginFormProps extends FormikProps<any> {
  close: React.MouseEventHandler<any>;
  onForgotPassword: React.MouseEventHandler<any>;
}

const ResetPasswordContent = (props: { handleClose: () => void; submit: Function; }) => {
  return (
    <DialogContent dividers>
      <Typography className="mb20" color="primary" component="strong">
        Forgot Password
      </Typography>
      <Typography className="mb20">
        Enter your email to receive a link to reset your password.
      </Typography>
      <ResetPasswordForm handleClose={props.handleClose} submit={props.submit} />
    </DialogContent>
  )
};

const LoginForm = ({close, isSubmitting, onForgotPassword, ...lcProps}: LoginFormProps) => {
  const actionsButtons: ButtonProps[] = [
    {
      className: 'floatLeft mt20 mb20',
      color: 'secondary',
      label: 'Cancel',
      onClick: close,
      size: 'medium',
      type: 'button',
      variant: 'contained',
    },
    {
      className: 'floatRight mt20 mb20',
      color: 'primary',
      disabled: isSubmitting,
      label: 'Login',
      loading: isSubmitting,
      size: 'medium',
      type: 'submit',
      variant: 'contained',
    }
  ];
  return (
    <>
      <LoginText />
      <Form className="labelShrink">
        <LoginFields {...lcProps} />
        <Grid container>
          <Grid container style={{justifyContent: 'space-between'}} className="mt15">
            <Button color="primary" onClick={onForgotPassword} label="Forgot Password?" />
          </Grid>
          <Grid container justifyContent="space-between">
            {
              actionsButtons.map((buttonProps, key) => (<Button key={key} {...buttonProps} />))
            }
          </Grid>
        </Grid>
      </Form>
    </>
  );
};

const LoginContent = (props: LoginContentProps) => {
  const {close, onForgotPassword, userType} = props;
  const {logout, mutateUser, user} = useAuth();
  const router = useRouter();
  const snackbar = useContext(SnackbarContext);
  const [showConfirmation, setShowConfirmation] = React.useState(initFalse);
  const onCancel = async () => {
    await logout()
    setShowConfirmation(initFalse);
  };
  const onConfirm = async () => {
    setShowConfirmation(initFalse);
    return router.push(Paths.AgentPortal);
  };
  const log = (a: string) => {
    snackbar.showMessage(a, SnackBarSeverity.Error);
    logout();
  };
  const onLoginSubmit = async ({email, password}: FormikValues, {resetForm}: FormikValues) => {
    try {
      // role: [ROLES.MultiLife, ROLES.Individual]
      const payload = {email, password, role: userType === LoginType.Agent ? ROLES.Agent : ROLES.Individual};
      const {accessToken, user} = await useLogin(payload);

      if (redirectIfPreferred(accessToken, user)) {
        snackbar.showMessage('Redirecting to Ilia...', SnackBarSeverity.Success);
        return;
      }

      await save(AUTH_SESSION_KEY, accessToken);
      await mutateUser(user);

      refreshAccessToken(log);

      const alreadySigned = checkSellerAgreementSignValid(user.agent.sellerAgreements);
      if (alreadySigned) {
        return router.push(Paths.AgentPortal);
      }

      await save(AGENT_SIGNING_KEY, stringify(alreadySigned));
      setShowConfirmation(!initFalse);

      resetForm();
      // @ts-ignore TS1196: Catch clause variable cannot have a type annotation.
    } catch (err: any) {
      const errMessage = handleFormError(err);
      snackbar.showMessage(errMessage, SnackBarSeverity.Error);
    }
  };
  const formProps = {
    children: LoginForm,
    close,
    initialValues: {email: '', password: ''},
    onForgotPassword,
    onSubmit: onLoginSubmit,
    validationSchema: Yup.object({
      email: Yup.string().email().required(REQUIRED),
      password: Yup.string().required(REQUIRED)
    })
  };

  React.useEffect(() => {
    fetcher().then(r => verifyLastSession(router, user || r));
  }, [user]);

  return (
    <DialogContent dividers>
      {
        showConfirmation &&
        <Agreement onCancel={onCancel} onConfirm={onConfirm} show={showConfirmation} />
      }
      {
        !showConfirmation &&
        <FormWrap {...formProps} />
      }
    </DialogContent>
  );
};

const LoginDialog: React.FC<LoginDialogProps> = ({userType, isOpen, close}: LoginDialogProps) => {
  const [content, setContent] = React.useState(Account.Login);
  const snackbar = useContext(SnackbarContext);
  const resetPassword = async (values: { email: string }, {setSubmitting}: any) => {
    try {
      const res = await forgotPassword(Object.assign(values));
      snackbar.showMessage(res.data, SnackBarSeverity.Success);
      setContent(Account.MessageResetPassword);
      // @ts-ignore TS1196: Catch clause variable cannot have a type annotation.
    } catch (e: any) {
      const errMessage = handleFormError(e);
      snackbar.showMessage(errMessage, SnackBarSeverity.Error);
    } finally {
      setSubmitting(initFalse);
    }
  }

  React.useEffect(() => {
    setContent(Account.Login);
  }, [isOpen]);

  return (
    <Dialog aria-labelledby="customized-dialog-title" fullWidth={true} maxWidth="sm" onClose={close} open={isOpen}>
      <DialogHeader onClose={close}>
        {userType} Login
      </DialogHeader>
      {
        content === Account.Login &&
        <LoginContent onForgotPassword={() => setContent(Account.ResetPassword)} close={close} userType={userType} />
      }

      {
        content === Account.ResetPassword &&
        <ResetPasswordContent submit={resetPassword} handleClose={close} />
      }

      {
        content === Account.MessageResetPassword &&
        <MessageResetPasswordContent handleClose={close} />
      }
    </Dialog>
  )
};

export default LoginDialog;
